import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { formatApiError } from "../lib/api";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import SEO from "../components/SEO";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultoriaSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: "" });
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", error: "Sesión no encontrada", attempts: 0, data: null }); return; }
    let cancelled = false;
    const MAX = 25;
    const poll = async (n = 0) => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API_BASE}/payments/public-status/${sessionId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(formatApiError(data.detail) || "Error");
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts: n, error: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, attempts: n, error: "" });
          return;
        }
        if (n >= MAX) {
          setState({ status: "timeout", data, attempts: n, error: "" });
          return;
        }
        setState({ status: "polling", attempts: n, data, error: "" });
        pollRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e.message, attempts: n, data: null });
      }
    };
    poll(0);
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <SEO title="Pago de Consultoría · Confirmación" path="/consultoria/exito" noindex />
      <SiteHeader />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        {state.status === "polling" && (
          <div className="bg-white border border-stone-200 p-10 rounded-sm text-center" data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-[#E0651F] animate-spin mx-auto mb-4" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-2">Verificación en curso</div>
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Confirmando tu pago…</h1>
            <p className="text-stone-600 text-sm">Estamos verificando con Stripe. Esto puede tardar unos segundos. Intento {state.attempts + 1} / 25.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div className="bg-white border-l-4 border-emerald-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mb-4" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-emerald-700 mb-2">Pago confirmado</div>
            <h1 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-4">¡Gracias! Hemos recibido tu pago y tu nota simple.</h1>
            <p className="text-stone-700 leading-relaxed mb-3">
              Importe abonado: <strong>{state.data?.amount?.toFixed(2)} €</strong>. Te hemos enviado un email de confirmación con todos los datos del pedido.
            </p>
            <p className="text-stone-700 leading-relaxed mb-6">
              Nuestro equipo empieza ya el análisis registral. Recibirás las conclusiones por escrito (cargas que se cancelan, cuáles subsisten, riesgos detectados y recomendación) en <strong>24-72 horas hábiles</strong>.
            </p>
            <div className="bg-stone-100 border-l-4 border-[#E0651F] p-5 mb-6">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-1">¿Necesitas adelantar algo?</div>
              <p className="text-sm text-stone-700">Responde al email de confirmación o escríbenos por los canales de abajo.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={buildWaUrl(WA_MSG.consultoria)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWhatsApp(WA_MSG.consultoria); }}
                className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="success-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href="mailto:informes@fmsubastas.com"
                className="inline-flex items-center gap-2 bg-white hover:bg-stone-100 text-[#2d1f1a] border border-stone-300 px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm">
                <Mail className="w-4 h-4" /> Email
              </a>
              <Link to="/" className="inline-flex items-center gap-2 text-[#E0651F] hover:underline px-2 py-3 text-xs tracking-[0.18em] uppercase font-bold">
                Volver al inicio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {(state.status === "expired" || state.status === "timeout") && (
          <div className="bg-white border-l-4 border-amber-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-pending">
            <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Estamos verificando tu pago…</h1>
            <p className="text-stone-700 leading-relaxed mb-6">
              El proceso está tardando más de lo habitual. Si tu cobro se ha realizado, recibirás un email de confirmación en cuanto Stripe nos notifique. Si en 10 minutos no recibes nada, contáctanos por WhatsApp.
            </p>
            <a href={buildWaUrl(WA_MSG.consultoria)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); openWhatsApp(WA_MSG.consultoria); }}
              className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
              data-testid="pending-whatsapp">
              <MessageCircle className="w-4 h-4" /> Contactar por WhatsApp
            </a>
          </div>
        )}

        {state.status === "error" && (
          <div className="bg-white border-l-4 border-red-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-error">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Algo no ha ido bien</h1>
            <p className="text-stone-700 mb-6">{state.error || "No hemos podido verificar el pago."}</p>
            <Link to="/consultoria/contratar" className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm">
              Volver a empezar
            </Link>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
